import React, { useState, useMemo } from 'react';
import { IMAGE_GATEWAY_URL } from '../config/images';

interface MenuItemImageProps {
  imageUrl?: string | null;
  itemName: string;
  size?: 'thumbnail' | 'card' | 'full';
  fallback?: 'emoji' | 'placeholder' | 'none';
}

const SIZE_PRESETS: Record<string, string> = {
  thumbnail: 'width=150,height=150,fit=cover,format=auto,quality=80',
  card: 'width=400,height=300,fit=cover,format=auto,quality=80',
  full: 'width=800,format=auto,quality=85',
};

// Skip image loading on very slow connections (2G / slow-2G)
const isSlowConnection = (): boolean => {
  const conn = (navigator as any).connection;
  if (!conn) return false;
  return conn.effectiveType === '2g' || conn.effectiveType === 'slow-2g';
};

const PlaceholderSvg: React.FC<{ itemName: string }> = React.memo(({ itemName }) => (
  <div className="w-full h-full flex items-center justify-center bg-gray-100 rounded-md" role="img" aria-label={itemName}>
    <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect width="48" height="48" rx="8" fill="#E5E7EB" />
      <path d="M14 34L20 26L24 30L30 22L34 34H14Z" fill="#9CA3AF" />
      <circle cx="20" cy="18" r="3" fill="#9CA3AF" />
    </svg>
  </div>
));
PlaceholderSvg.displayName = 'PlaceholderSvg';

const MenuItemImage: React.FC<MenuItemImageProps> = React.memo(({ imageUrl, itemName, size = 'card', fallback = 'none' }) => {
  const [loaded, setLoaded] = useState(false);
  const [errored, setErrored] = useState(false);

  const src = useMemo(() => {
    if (!imageUrl || isSlowConnection()) return null;
    const params = SIZE_PRESETS[size] || SIZE_PRESETS.card;
    // If imageUrl is a relative key (not a full URL), prefix with gateway URL
    const fullUrl = imageUrl.startsWith('http') ? imageUrl : `${IMAGE_GATEWAY_URL}/${imageUrl}`;
    return `https://scan2order.shop/cdn-cgi/image/${params}/${fullUrl}`;
  }, [imageUrl, size]);

  if (!src || errored) {
    if (fallback === 'placeholder') return <PlaceholderSvg itemName={itemName} />;
    return null; // parent shows its own fallback (emoji etc.)
  }

  return (
    <>
      {!loaded && fallback === 'placeholder' && <PlaceholderSvg itemName={itemName} />}
      <img
        src={src}
        alt={itemName}
        loading="lazy"
        decoding="async"
        fetchPriority={size === 'thumbnail' ? 'low' : 'auto'}
        onLoad={() => setLoaded(true)}
        onError={() => setErrored(true)}
        className="w-full h-full object-cover rounded-md"
        style={{
          opacity: loaded ? 1 : 0,
          transition: 'opacity 0.3s ease-in-out',
          ...(fallback === 'placeholder' && !loaded ? { position: 'absolute', top: 0, left: 0 } : {}),
        }}
      />
    </>
  );
});

MenuItemImage.displayName = 'MenuItemImage';

export default MenuItemImage;
